import Phaser from 'phaser';
import type { ArenaWaveEntry, EnemyType } from '../core/types';
import { arenaWavePreview, arenaWaveSize } from '../core/arenaWave';
import { BALANCE } from '../config/balance';
import { enemyNameWithVariant } from '../config/displayNames';
import { enemySpecial, enemyStatLine } from './enemyInfo';
import { textureKeyFor } from './EnemyView';

/**
 * The Test Arena's wave builder: add any number of enemy types to a MIX, set a
 * single gap for the whole wave, and send it. The enemies are interleaved rather
 * than marched out group by group (3×A + 3×B walks out as ABABAB), and the
 * SPAWN ORDER strip previews exactly that — it is the only way to see the mix
 * before committing to it. Two SANDBOX RULES checkboxes mute the enemies'
 * anti-tower abilities (the Null-Hex stun and the Veggie-Hex fire-rate slow)
 * for the wave; the choice is remembered for the rest of the session.
 *
 * Built on the house modal recipe (full-screen scrim + panel + teardown by
 * destroying a pushed object array). This module only builds the dialog — the
 * host scene owns any pause/resume and the sim dispatch, via the callbacks.
 */

/** Per-entry size bounds. The cap is a sanity ceiling, not game balance. */
const COUNT_MIN = 1;
const COUNT_MAX = 9999;
/** Spacing bounds, in seconds. The floor is deliberately above zero. */
const INTERVAL_MIN = 0.1;
const INTERVAL_MAX = 10;

export const ARENA_DEFAULT_COUNT = 5;
export const ARENA_DEFAULT_INTERVAL = 1;

/** MIX rows visible at once — the list scrolls when the mix grows past this. */
const MIX_VISIBLE = 6;

/** Enemies previewed in the SPAWN ORDER strip before it elides the rest. */
const PREVIEW_MAX = 14;

/** Scenes with a dialog already open — prevents stacked modals per scene. */
const openScenes = new WeakSet<Phaser.Scene>();

/** The wave's ability toggles — see GameCommand's startArenaWave. */
export interface ArenaWaveMods {
  /** Mute the Null Hex: no enemy stuns any tower this wave. */
  disableStun: boolean;
  /** Mute the Veggie Hex: no enemy slows any tower's fire rate this wave. */
  disableFireSlow: boolean;
  /**
   * Mute control effects: no enemy seals tower placement this wave (Emperor
   * Pepro's Imperial Decree). Shown inverted as a "Control effects" checkbox
   * that starts CHECKED — effects are live by default.
   */
  disableControl: boolean;
  /**
   * The Moon Arena's "Friendly": attackers stay peaceful — no leaves, no
   * nugget pieces, no fireballs at any tower this wave. Only offered there.
   */
  disableTowerFire: boolean;
  /**
   * The Hub arenas' "Disable boss abilities": no chips or whistles to press,
   * none of Prince Nacho's spears, lasers or shields, no Reaper scythe.
   */
  disableBossAbility: boolean;
}

/**
 * Session memory for the SANDBOX RULES checkboxes: reopening the dialog keeps
 * the previous choice, a fresh page load starts with every ability live.
 */
let lastMods: ArenaWaveMods = {
  disableStun: false,
  disableFireSlow: false,
  disableControl: false,
  disableTowerFire: false,
  disableBossAbility: false,
};

export interface ArenaWaveOpts {
  /** Enemies the player may choose from (unlocked episodes only). */
  types: EnemyType[];
  /** Send one mixed wave. Never called with an empty entry list. */
  onStart: (entries: ArenaWaveEntry[], interval: number, mods: ArenaWaveMods) => void;
  onClose?: () => void;
  /** Offer the "Friendly" toggle (The Hub's arenas). */
  friendly?: boolean;
  /** Offer the "Disable boss abilities" toggle (The Hub's arenas). */
  bossToggle?: boolean;
  /** The send button's word (the Level Creator says SAVE). Default START WAVE. */
  submitLabel?: string;
  /** Most rows the MIX may hold (the Level Creator: 60). Default unlimited. */
  maxEntries?: number;
  /** Open on an existing wave (the Level Creator editing one of its waves). */
  initial?: { entries: ArenaWaveEntry[]; interval: number; mods: Partial<ArenaWaveMods> };
}

const W = 1280;
const H = 720;
const D = 100;

/**
 * Enemy-selector geometry. The name is centred at NAME_CX between the ‹ › arrows
 * and capped to NAME_MAX_W — long names ("King Nugget-tron") would otherwise
 * grow out from the centre and swallow the arrows.
 */
const NAME_CX = 550;
const NAME_MAX_W = 210;

/** MIX list rows. */
const MIX_Y0 = 348;
const MIX_STRIDE = 20;

/** SPAWN ORDER strip. */
const PREVIEW_X0 = 372;
const PREVIEW_Y = 534;
const PREVIEW_ICON = 24;
/**
 * The strip's icon spacing IS the wave's gap, to scale: this many pixels per
 * second of gap between neighbouring spawns. Wide gaps therefore fit fewer
 * icons before PREVIEW_X_MAX and the "+N" elide picks up the rest.
 */
const PREVIEW_PX_PER_SEC = 14;
/** Right edge the strip may reach (the panel spans 230..1050). */
const PREVIEW_X_MAX = 1000;

/** SANDBOX RULES checkboxes (right column, below the enemy art). */
const RULES_X = 800;
const RULES_TITLE_Y = 316;
const RULES_ROW0_Y = 350;
const RULES_ROW_STRIDE = 38;

/** Clamp helper shared by the steppers and the keypad. */
function clamp(v: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, v));
}

/**
 * One tap of the gap stepper: coarse 0.5s steps above half a second, fine 0.1s
 * steps at/below it — …1.5 → 1.0 → 0.5 → 0.4 → 0.3 → 0.2 → 0.1 (and back up).
 * Rounded to one decimal so repeated float steps never drift.
 */
function stepInterval(v: number, dir: -1 | 1): number {
  const fine = dir < 0 ? v <= 0.5 : v < 0.5;
  const next = Math.round((v + dir * (fine ? 0.1 : 0.5)) * 10) / 10;
  return clamp(next, INTERVAL_MIN, INTERVAL_MAX);
}

/**
 * "3× Nugget Trooper" — one entry of the mix, in plain words. Variant-noted, so
 * a mix holding both Pepros reads "1× Emperor Pepro" and
 * "1× Emperor Pepro (Debuffed)" rather than the same line twice.
 */
export function describeEntry(e: ArenaWaveEntry): string {
  return `${e.count}× ${enemyNameWithVariant(e.enemyType)}`;
}

/**
 * A small sub-modal describing one enemy: art, stats, and plain-English
 * abilities. Opened by the red "?" beside the enemy selector.
 */
function openEnemyGuide(scene: Phaser.Scene, type: EnemyType): void {
  const GD = D + 20; // above the wave dialog
  const objs: Phaser.GameObjects.GameObject[] = [];
  const spec = BALANCE.enemies[type];

  objs.push(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.72)
      .setInteractive()
      .setDepth(GD),
  );
  objs.push(
    scene.add
      .rectangle(W / 2, H / 2, 620, 380, 0x1a1726, 1)
      .setStrokeStyle(3, 0xcc4444)
      .setDepth(GD + 1),
  );
  objs.push(
    scene.add
      .text(W / 2, 220, 'ENEMY GUIDE', { fontSize: '20px', color: '#e8674f', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(GD + 2),
  );

  const artKey = textureKeyFor(type);
  if (scene.textures.exists(artKey)) {
    objs.push(
      scene.add
        .image(430, 330, artKey)
        .setDisplaySize(110, 110)
        .setDepth(GD + 2),
    );
  }
  objs.push(
    scene.add
      .text(510, 288, enemyNameWithVariant(type), {
        fontSize: '24px',
        color: '#ffffff',
        fontStyle: 'bold',
      })
      .setDepth(GD + 2),
  );
  objs.push(
    scene.add
      .text(510, 322, enemyStatLine(spec), { fontSize: '15px', color: '#9fd8ff' })
      .setDepth(GD + 2),
  );

  const special = enemySpecial(spec);
  objs.push(
    scene.add
      .text(
        370,
        400,
        special ? `Abilities: ${special}` : 'No special abilities — it just marches.',
        {
          fontSize: '14px',
          color: special ? '#e8a0a0' : '#9aa6ad',
          wordWrap: { width: 540 },
        },
      )
      .setDepth(GD + 2),
  );

  const btn = scene.add
    .rectangle(W / 2, 490, 180, 44, 0x1a6b1a)
    .setStrokeStyle(2, 0x4caf50)
    .setInteractive({ useHandCursor: true })
    .setDepth(GD + 2);
  objs.push(btn);
  objs.push(
    scene.add
      .text(W / 2, 490, 'Got it!', { fontSize: '17px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(GD + 3),
  );
  btn.on('pointerup', () => objs.forEach((o) => o.destroy()));
}

/**
 * A numeric keypad sub-modal for typing a count directly (tapping the count
 * opens it). On-screen digits work on touch; a physical keyboard's digits,
 * Backspace, Enter and Escape drive it too. OK clamps to COUNT_MIN..COUNT_MAX;
 * an empty value or CANCEL keeps the old count. Returns a close() function so
 * the parent dialog can tear it down if IT closes first.
 */
function openCountPad(scene: Phaser.Scene, initial: number, onCommit: (n: number) => void) {
  const PD = D + 40; // above the wave dialog AND the enemy guide
  const objs: Phaser.GameObjects.GameObject[] = [];
  let typed = '';

  objs.push(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.6)
      .setInteractive()
      .setDepth(PD),
  );
  objs.push(
    scene.add
      .rectangle(W / 2, 378, 320, 440, 0x1a1726, 1)
      .setStrokeStyle(3, 0x4488cc)
      .setDepth(PD + 1),
  );
  objs.push(
    scene.add
      .text(W / 2, 192, 'HOW MANY', { fontSize: '18px', color: '#4488cc', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(PD + 2),
  );

  objs.push(
    scene.add
      .rectangle(W / 2, 236, 260, 44, 0x0d0b14, 1)
      .setStrokeStyle(2, 0x8890a0)
      .setDepth(PD + 2),
  );
  const valueText = scene.add
    .text(W / 2, 236, '', { fontSize: '26px', color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(PD + 3);
  objs.push(valueText);

  const showValue = (): void => {
    // The old count shows greyed until the first digit replaces it.
    valueText.setText(typed === '' ? String(initial) : typed);
    valueText.setColor(typed === '' ? '#5a6470' : '#ffffff');
  };

  const press = (key: string): void => {
    if (key >= '0' && key <= '9') {
      if (typed.length < String(COUNT_MAX).length) typed += key;
    } else if (key === 'back') {
      typed = typed.slice(0, -1);
    }
    showValue();
  };

  const teardown = (): void => {
    scene.input.keyboard?.off('keydown', onKey);
    objs.forEach((o) => o.destroy());
  };
  const commit = (): void => {
    const n = typed === '' ? initial : clamp(parseInt(typed, 10), COUNT_MIN, COUNT_MAX);
    teardown();
    onCommit(n);
  };

  const mkPadButton = (x: number, y: number, label: string, onTap: () => void): void => {
    const bg = scene.add
      .rectangle(x, y, 76, 50, 0x2a2a33)
      .setStrokeStyle(2, 0x8890a0)
      .setInteractive({ useHandCursor: true })
      .setDepth(PD + 2);
    objs.push(bg);
    objs.push(
      scene.add
        .text(x, y, label, { fontSize: '20px', color: '#ffffff', fontStyle: 'bold' })
        .setOrigin(0.5)
        .setDepth(PD + 3),
    );
    bg.on('pointerup', onTap);
  };

  // Phone-pad rows: 123 / 456 / 789 / ⌫0(blank).
  const COLS = [W / 2 - 84, W / 2, W / 2 + 84];
  const ROWS = [292, 350, 408, 466];
  for (let d = 1; d <= 9; d++) {
    const key = String(d);
    mkPadButton(COLS[(d - 1) % 3], ROWS[Math.floor((d - 1) / 3)], key, () => press(key));
  }
  mkPadButton(COLS[0], ROWS[3], '⌫', () => press('back'));
  mkPadButton(COLS[1], ROWS[3], '0', () => press('0'));

  const okBg = scene.add
    .rectangle(W / 2 - 55, 534, 140, 46, 0x1a6b1a)
    .setStrokeStyle(2, 0x4caf50)
    .setInteractive({ useHandCursor: true })
    .setDepth(PD + 2);
  objs.push(okBg);
  objs.push(
    scene.add
      .text(W / 2 - 55, 534, 'OK', { fontSize: '17px', color: '#ffffff', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(PD + 3),
  );
  okBg.on('pointerup', commit);

  const cancelBg = scene.add
    .rectangle(W / 2 + 82, 534, 110, 46, 0x2a2a33)
    .setStrokeStyle(2, 0x8890a0)
    .setInteractive({ useHandCursor: true })
    .setDepth(PD + 2);
  objs.push(cancelBg);
  objs.push(
    scene.add
      .text(W / 2 + 82, 534, 'CANCEL', { fontSize: '14px', color: '#cdd6dd', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(PD + 3),
  );
  cancelBg.on('pointerup', teardown);

  function onKey(ev: KeyboardEvent): void {
    if (ev.key >= '0' && ev.key <= '9') press(ev.key);
    else if (ev.key === 'Backspace') press('back');
    else if (ev.key === 'Enter') commit();
    else if (ev.key === 'Escape') teardown();
  }
  scene.input.keyboard?.on('keydown', onKey);

  showValue();
  return teardown;
}

/**
 * Open the arena wave builder over `scene`. Returns false (and does nothing) if
 * one is already open there, or if `types` is empty.
 */
export function openArenaWaveDialog(scene: Phaser.Scene, opts: ArenaWaveOpts): boolean {
  if (openScenes.has(scene) || opts.types.length === 0) return false;
  openScenes.add(scene);
  scene.events.once('shutdown', () => openScenes.delete(scene));

  let typeIndex = 0;
  let count = ARENA_DEFAULT_COUNT;
  /** One gap for the whole wave — the enemies are one interleaved stream. */
  let interval = ARENA_DEFAULT_INTERVAL;
  /** Enemy types added to the mix, in the order they were added. */
  const mix: ArenaWaveEntry[] = [];
  /** Index of the first visible MIX row — the list windows over the full mix. */
  let mixScroll = 0;
  /** This wave's ability toggles, seeded from the session's last choice. */
  const mods: ArenaWaveMods = { ...lastMods };
  // A toggle this arena does not offer can never be on unseen: a box ticked
  // in a Hub arena must not quietly carry into the Training Stadium.
  if (!opts.friendly) mods.disableTowerFire = false;
  if (!opts.bossToggle) mods.disableBossAbility = false;
  if (opts.initial) {
    mix.push(...opts.initial.entries.map((e) => ({ ...e })));
    interval = opts.initial.interval;
    for (const k of Object.keys(mods) as (keyof ArenaWaveMods)[])
      mods[k] = opts.initial.mods[k] ?? false;
  }
  /** Close function of the count keypad while one is up (torn down with us). */
  let closePad: (() => void) | null = null;

  const objs: Phaser.GameObjects.GameObject[] = [];
  /** MIX rows + preview icons — rebuilt on every change. */
  let listObjs: Phaser.GameObjects.GameObject[] = [];

  objs.push(
    scene.add
      .rectangle(W / 2, H / 2, W, H, 0x000000, 0.72)
      .setInteractive()
      .setDepth(D),
  );
  objs.push(
    scene.add
      .rectangle(W / 2, 378, 820, 540, 0x1a1726, 1)
      .setStrokeStyle(3, 0xf5c518)
      .setDepth(D + 1),
  );
  objs.push(
    scene.add
      .text(W / 2, 136, 'BUILD WAVE', { fontSize: '24px', color: '#f5c518', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(D + 2),
  );

  // Enemy art — a dimmed backdrop on the right, so the chosen enemy is visible
  // behind the controls without competing with them.
  const art = scene.add
    .image(900, 205, 'enemy-grunt')
    .setDisplaySize(170, 170)
    .setAlpha(0.38)
    .setDepth(D + 1.2);
  objs.push(art);

  const nameText = scene.add
    .text(NAME_CX, 182, '', { fontSize: '22px', color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(D + 2);
  objs.push(nameText);

  // The count is tappable — a boxed number that opens the keypad — because a
  // stepper alone is hopeless across a 1..9999 range.
  const countBox = scene.add
    .rectangle(560, 232, 120, 38, 0x0d0b14, 1)
    .setStrokeStyle(2, 0x8890a0)
    .setInteractive({ useHandCursor: true })
    .setDepth(D + 1.5);
  objs.push(countBox);
  const countText = scene.add
    .text(560, 232, '', { fontSize: '22px', color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(D + 2);
  objs.push(countText);

  const intervalText = scene.add
    .text(560, 490, '', { fontSize: '22px', color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(D + 2);
  objs.push(intervalText);

  const mixTitle = scene.add
    .text(290, 324, '', { fontSize: '14px', color: '#f5c518', fontStyle: 'bold' })
    .setDepth(D + 2);
  objs.push(mixTitle);

  const previewLabel = scene.add
    .text(290, PREVIEW_Y, 'Order', { fontSize: '14px', color: '#9aa6ad' })
    .setOrigin(0, 0.5)
    .setDepth(D + 2);
  objs.push(previewLabel);

  const addBg = scene.add
    .rectangle(390, 282, 180, 42, 0x1a3a7a)
    .setStrokeStyle(2, 0x4488cc)
    .setInteractive({ useHandCursor: true })
    .setDepth(D + 2);
  objs.push(addBg);
  const addLabel = scene.add
    .text(390, 282, '+ ADD ENEMY', { fontSize: '15px', color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(D + 3);
  objs.push(addLabel);

  const clearBg = scene.add
    .rectangle(570, 282, 130, 42, 0x2a2a33)
    .setStrokeStyle(2, 0x8890a0)
    .setInteractive({ useHandCursor: true })
    .setDepth(D + 2);
  objs.push(clearBg);
  const clearLabel = scene.add
    .text(570, 282, 'CLEAR MIX', { fontSize: '13px', color: '#cdd6dd', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(D + 3);
  objs.push(clearLabel);

  const startBg = scene.add
    .rectangle(460, 600, 220, 50, 0x1a6b1a)
    .setStrokeStyle(2, 0x4caf50)
    .setInteractive({ useHandCursor: true })
    .setDepth(D + 2)
    .setName('arena-wave-start');
  objs.push(startBg);
  const startLabel = scene.add
    .text(460, 600, '', { fontSize: '18px', color: '#ffffff', fontStyle: 'bold' })
    .setOrigin(0.5)
    .setDepth(D + 3);
  objs.push(startLabel);

  /** The entry currently configured by the selector + count stepper. */
  const current = (): ArenaWaveEntry => ({ enemyType: opts.types[typeIndex], count });

  /**
   * What START will actually send: the mix, or — when nothing was added — the
   * entry currently on screen, so the common single-enemy case stays one tap.
   */
  const pending = (): ArenaWaveEntry[] => (mix.length ? mix : [current()]);

  // ── MIX scroll buttons — visible only when the list overflows ─────────────
  const mkScrollButton = (y: number, glyph: string, dir: -1 | 1): Phaser.GameObjects.Text => {
    const t = scene.add
      .text(540, y, glyph, {
        fontSize: '20px',
        color: '#f5c518',
        fontStyle: 'bold',
        padding: { x: 10, y: 4 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    t.on('pointerup', () => {
      mixScroll += dir;
      refresh();
    });
    objs.push(t);
    return t;
  };
  const scrollUpBtn = mkScrollButton(MIX_Y0 + 8, '▲', -1);
  const scrollDownBtn = mkScrollButton(MIX_Y0 + (MIX_VISIBLE - 1) * MIX_STRIDE - 4, '▼', 1);

  // Mouse-wheel scrolling for the MIX list while the dialog is up.
  const onWheel = (_p: Phaser.Input.Pointer, _over: unknown, _dx: number, dy: number): void => {
    if (mix.length <= MIX_VISIBLE || dy === 0) return;
    mixScroll += dy > 0 ? 1 : -1;
    refresh();
  };
  scene.input.on('wheel', onWheel);

  // ── SANDBOX RULES — per-wave ability toggles ──────────────────────────────
  objs.push(
    scene.add
      .text(RULES_X, RULES_TITLE_Y, 'SANDBOX RULES', {
        fontSize: '14px',
        color: '#f5c518',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5)
      .setDepth(D + 2),
  );

  const checks: Phaser.GameObjects.Text[] = [];
  const mkToggle = (row: number, label: string, get: () => boolean, flip: () => void): void => {
    const y = RULES_ROW0_Y + row * RULES_ROW_STRIDE;
    const box = scene.add
      .rectangle(RULES_X + 11, y, 22, 22, 0x0d0b14, 1)
      .setStrokeStyle(2, 0x8890a0)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objs.push(box);
    const check = scene.add
      .text(RULES_X + 11, y, '✓', { fontSize: '18px', color: '#4caf50', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(D + 3)
      .setVisible(get());
    objs.push(check);
    checks.push(check);
    const text = scene.add
      .text(RULES_X + 30, y, label, {
        fontSize: '14px',
        color: '#cdd6dd',
        padding: { x: 4, y: 6 },
      })
      .setOrigin(0, 0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objs.push(text);
    const onTap = (): void => {
      flip();
      check.setVisible(get());
      // Remember for the next dialog this session.
      lastMods = { ...mods };
    };
    box.on('pointerup', onTap);
    text.on('pointerup', onTap);
  };
  mkToggle(
    0,
    'No tower stuns',
    () => mods.disableStun,
    () => (mods.disableStun = !mods.disableStun),
  );
  mkToggle(
    1,
    'No fire-rate slows',
    () => mods.disableFireSlow,
    () => (mods.disableFireSlow = !mods.disableFireSlow),
  );
  // Inverted sense on purpose: the box reads "Control effects" and starts
  // CHECKED (enabled). Unticking it disables control effects — e.g. Emperor
  // Pepro's placement seal — for this wave.
  mkToggle(
    2,
    'Control effects',
    () => !mods.disableControl,
    () => (mods.disableControl = !mods.disableControl),
  );
  // The Moon Arena only: peaceful attackers.
  if (opts.friendly) {
    mkToggle(
      3,
      'Friendly (no damage)',
      () => mods.disableTowerFire,
      () => (mods.disableTowerFire = !mods.disableTowerFire),
    );
  }
  if (opts.bossToggle) {
    mkToggle(
      4,
      'Disable boss abilities',
      () => mods.disableBossAbility,
      () => (mods.disableBossAbility = !mods.disableBossAbility),
    );
  }

  const refresh = (): void => {
    const type = opts.types[typeIndex];
    // Reset the scale before measuring, or each shrink would compound.
    nameText.setScale(1).setText(enemyNameWithVariant(type));
    if (nameText.width > NAME_MAX_W) nameText.setScale(NAME_MAX_W / nameText.width);
    countText.setText(String(count));
    intervalText.setText(`${interval.toFixed(1)}s`);
    const key = textureKeyFor(type);
    if (scene.textures.exists(key)) art.setTexture(key).setVisible(true);
    else art.setVisible(false);

    clearBg.setVisible(mix.length > 0);
    clearLabel.setVisible(mix.length > 0);

    const total = arenaWaveSize(pending());
    startLabel.setText(`${opts.submitLabel ?? 'START WAVE'} · ${total}`);

    // The MIX list is a MIX_VISIBLE-row window over the full mix; the title
    // reports the window so an off-screen tail is never a surprise.
    mixScroll = clamp(mixScroll, 0, Math.max(0, mix.length - MIX_VISIBLE));
    const overflow = mix.length > MIX_VISIBLE;
    scrollUpBtn.setVisible(overflow).setAlpha(mixScroll > 0 ? 1 : 0.3);
    scrollDownBtn.setVisible(overflow).setAlpha(mixScroll < mix.length - MIX_VISIBLE ? 1 : 0.3);
    const windowNote = overflow
      ? ` · ${mixScroll + 1}–${Math.min(mix.length, mixScroll + MIX_VISIBLE)} of ${mix.length}`
      : '';
    mixTitle.setText(
      mix.length
        ? `MIX — ${total} enemies, spawned in turn${windowNote}`
        : 'MIX — empty (START sends the enemy above)',
    );

    listObjs.forEach((o) => o.destroy());
    listObjs = mix.slice(mixScroll, mixScroll + MIX_VISIBLE).map((e, i) =>
      scene.add
        .text(290, MIX_Y0 + i * MIX_STRIDE, `${mixScroll + i + 1}.  ${describeEntry(e)}`, {
          fontSize: '14px',
          color: '#cdd6dd',
        })
        .setDepth(D + 2),
    );

    // SPAWN ORDER — the actual interleaved order, drawn as art so a mix reads at
    // a glance (ABABAB rather than AAABBB), spaced to scale with the chosen gap
    // (PREVIEW_PX_PER_SEC) so tightening the gap visibly packs the strip. Elided
    // once the row is full (or past PREVIEW_MAX); computed via the preview
    // helper so a 9999-count mix never materialises in full.
    const stride = PREVIEW_ICON + 2 + Math.round(interval * PREVIEW_PX_PER_SEC);
    const fit = 1 + Math.floor((PREVIEW_X_MAX - PREVIEW_X0) / stride);
    const order = arenaWavePreview(pending(), Math.min(PREVIEW_MAX, Math.max(1, fit)));
    order.forEach((t, i) => {
      const k = textureKeyFor(t);
      if (!scene.textures.exists(k)) return;
      const x = PREVIEW_X0 + i * stride;
      // A faint tick between neighbours makes the spacing read as time, not as
      // a layout accident. Skipped when the gap is too tight to show one.
      const tick = stride - PREVIEW_ICON - 8;
      if (i > 0 && tick >= 4) {
        listObjs.push(
          scene.add.rectangle(x - stride / 2, PREVIEW_Y, tick, 2, 0x8890a0, 0.35).setDepth(D + 2),
        );
      }
      listObjs.push(
        scene.add
          .image(x, PREVIEW_Y, k)
          .setDisplaySize(PREVIEW_ICON, PREVIEW_ICON)
          .setDepth(D + 2),
      );
    });
    if (total > order.length) {
      const lastX = PREVIEW_X0 + (order.length - 1) * stride;
      listObjs.push(
        scene.add
          .text(lastX + PREVIEW_ICON / 2 + 8, PREVIEW_Y, `+${total - order.length}`, {
            fontSize: '14px',
            color: '#9aa6ad',
          })
          .setOrigin(0, 0.5)
          .setDepth(D + 2),
      );
    }
  };

  /** A padded text button — padding gives Phaser a finger-sized hit area. */
  const mkTextButton = (
    x: number,
    y: number,
    glyph: string,
    size: number,
    color: string,
    onTap: () => void,
  ): void => {
    const t = scene.add
      .text(x, y, glyph, {
        fontSize: `${size}px`,
        color,
        fontStyle: 'bold',
        padding: { x: 12, y: 8 },
      })
      .setOrigin(0.5)
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    t.on('pointerup', onTap);
    objs.push(t);
  };

  // Row 1 — enemy selector, with the red "?" guide beside it.
  objs.push(
    scene.add
      .text(290, 182, 'Enemy', { fontSize: '16px', color: '#9aa6ad' })
      .setOrigin(0, 0.5)
      .setDepth(D + 2),
  );
  // Arrows sit outside the name's capped width (550 ± 105) with a margin.
  mkTextButton(410, 182, '‹', 34, '#ffffff', () => {
    typeIndex = (typeIndex - 1 + opts.types.length) % opts.types.length;
    refresh();
  });
  mkTextButton(690, 182, '›', 34, '#ffffff', () => {
    typeIndex = (typeIndex + 1) % opts.types.length;
    refresh();
  });
  mkTextButton(752, 182, '?', 24, '#ff6b6b', () => openEnemyGuide(scene, opts.types[typeIndex]));

  // Row 2 — how many of the selected enemy. Tap the number to type it.
  objs.push(
    scene.add
      .text(290, 232, 'How many', { fontSize: '16px', color: '#9aa6ad' })
      .setOrigin(0, 0.5)
      .setDepth(D + 2),
  );
  mkTextButton(478, 232, '−', 30, '#ffffff', () => {
    count = clamp(count - 1, COUNT_MIN, COUNT_MAX);
    refresh();
  });
  mkTextButton(642, 232, '+', 30, '#ffffff', () => {
    count = clamp(count + 1, COUNT_MIN, COUNT_MAX);
    refresh();
  });
  countBox.on('pointerup', () => {
    if (closePad) return; // one keypad at a time
    const done = openCountPad(scene, count, (n) => {
      closePad = null;
      count = n;
      refresh();
    });
    closePad = () => {
      done();
      closePad = null;
    };
  });

  // Row 3 — the wave's single gap. It sits BELOW the mix, not beside the enemy
  // picker, because it applies to the whole wave rather than to one entry.
  objs.push(
    scene.add
      .text(290, 490, 'Gap (whole wave)', { fontSize: '16px', color: '#9aa6ad' })
      .setOrigin(0, 0.5)
      .setDepth(D + 2),
  );
  mkTextButton(490, 490, '−', 30, '#ffffff', () => {
    interval = stepInterval(interval, -1);
    refresh();
  });
  mkTextButton(630, 490, '+', 30, '#ffffff', () => {
    interval = stepInterval(interval, +1);
    refresh();
  });

  addBg.on('pointerup', () => {
    // The Level Creator caps a wave's mix (its server refuses more).
    if (opts.maxEntries !== undefined && mix.length >= opts.maxEntries) {
      const note = scene.add
        .text(W / 2, 560, `A wave can mix ${opts.maxEntries} enemy types at most`, {
          fontSize: '15px',
          color: '#ffcf6b',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 5)
        .setName('arena-wave-full');
      scene.time.delayedCall(1600, () => note.destroy());
      return;
    }
    mix.push(current());
    // Snap the window to the end so the row just added is always visible.
    mixScroll = Math.max(0, mix.length - MIX_VISIBLE);
    refresh();
  });
  clearBg.on('pointerup', () => {
    mix.length = 0;
    mixScroll = 0;
    refresh();
  });

  const teardown = (): void => {
    closePad?.();
    scene.input.off('wheel', onWheel);
    listObjs.forEach((o) => o.destroy());
    listObjs = [];
    objs.forEach((o) => o.destroy());
    openScenes.delete(scene);
    opts.onClose?.();
  };

  startBg.on('pointerup', () => {
    const entries = pending();
    const gap = interval;
    const chosen = { ...mods };
    teardown();
    opts.onStart(entries, gap, chosen);
  });

  const cancelBg = scene.add
    .rectangle(700, 600, 160, 50, 0x2a2a33)
    .setStrokeStyle(2, 0x8890a0)
    .setInteractive({ useHandCursor: true })
    .setDepth(D + 2);
  objs.push(cancelBg);
  objs.push(
    scene.add
      .text(700, 600, 'CANCEL', { fontSize: '17px', color: '#cdd6dd', fontStyle: 'bold' })
      .setOrigin(0.5)
      .setDepth(D + 3),
  );
  cancelBg.on('pointerup', teardown);

  refresh();
  return true;
}
